class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      searchQuery: "[data-query]",
      searchMessage: "[data-message]",
      input: "[data-search-input]",
      submit: 'button[type="submit"]',
      loading: "[data-spinner]",
      clear: "[data-clear-search]",
      popularSearchItems: ["[data-ps-item]"],
      moreResultIcon: "[data-more-result-icon]",
    };

    this.container = document.querySelector("[data-search-popup]");

    this.domNodes = queryDomNodes(this.selectors, this.container);

    this.cachedResults = {};
    this.transitionDuration = 300;
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    this.searchPopupSearults = this.querySelector(".m-search-popup__result");
    this.popularSearches = this.querySelector("[data-popular-searches]");
    this.searchCount = this.querySelector("[data-search-count]");

    this.setupEventListeners();
  }

  setPopularSearchesLink = () => {
    const { popularSearchItems } = this.domNodes;
    popularSearchItems.forEach((itm) => (itm.href = createSearchLink(itm.dataset.psQuery)));
  };

  setupEventListeners() {
    const { clear } = this.domNodes;
    const form = this.querySelector("form.m-search-form");
    form.addEventListener("submit", this.onFormSubmit.bind(this));

    this.input.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
      }, 300).bind(this)
    );
    clear.addEventListener("click", this.onClearSearch.bind(this));
    MinimogEvents.subscribe(MinimogTheme.pubSubEvents.closeSearchPopup, () => this.close(true));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onClearSearch(event) {
    event.preventDefault();
    this.input.value = "";
    this.onChange();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.toggleSpinnerLoading(true);
    this.toggleClearSearch(false);

    const searchByTag = this.dataset.searchByTag === "true";
    const searchByBody = this.dataset.searchByBody === "true";
    const unavailableProductsOption = this.dataset.unavailableProductsOption;

    let searchFields = "title,product_type,vendor,variants.sku,variants.title";

    if (searchByTag) searchFields += ",body";
    if (searchByBody) searchFields += ",tag";

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const searchURL = `${window.MinimogSettings.routes.predictive_search_url}?q=${encodeURIComponent(
      searchTerm
    )}&resources[options][unavailable_products]=${unavailableProductsOption}&resources[options][fields]=${searchFields}&section_id=predictive-search`;

    fetch(searchURL)
      .then((response) => {
        if (!response.ok) {
          // Shopify Markets blocks predictive_search with 417 on some stores.
          if (response.status === 417 || response.status === 422 || response.status === 404) {
            return this.fallbackSearchViaProducts(searchTerm).then((markup) => ({
              __fallback: true,
              markup,
            }));
          }
          throw new Error(response.status);
        }
        return response.text();
      })
      .then((payload) => {
        if (payload && payload.__fallback) {
          this.cachedResults[queryKey] = payload.markup;
          this.renderSearchResults(payload.markup);
          return;
        }
        const doc = new DOMParser().parseFromString(payload, "text/html");
        const sectionEl =
          doc.querySelector("#shopify-section-predictive-search") ||
          doc.querySelector('[id^="shopify-section-"]');
        const resultsMarkup = sectionEl ? sectionEl.innerHTML : payload;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        this.toggleSpinnerLoading(false);
        this.toggleClearSearch(true);
        this.close();
        if (window.console) console.warn("Predictive search failed:", error);
      });
  }

  fallbackSearchViaProducts(searchTerm) {
    const rootRaw =
      (window.MinimogSettings && window.MinimogSettings.routes && window.MinimogSettings.routes.root) || "/";
    const root = rootRaw.replace(/\/$/, "");
    const url = `${root}/products.json?limit=250`;
    return fetch(url)
      .then((r) => (r.ok ? r.json() : { products: [] }))
      .then((data) => {
        const products = (data && data.products) || [];
        const q = searchTerm.toLowerCase().trim();
        const matches = products
          .filter((p) => {
            const haystack = [
              p.title,
              p.handle,
              p.product_type,
              p.vendor,
              (p.tags || []).join(" "),
              (p.variants || []).map((v) => v.title + " " + (v.sku || "")).join(" "),
            ]
              .join(" ")
              .toLowerCase();
            return haystack.indexOf(q) !== -1;
          })
          .slice(0, 8);
        return this.buildFallbackMarkup(matches, root);
      })
      .catch(() => this.buildFallbackMarkup([], ""));
  }

  buildFallbackMarkup(products, root) {
    const escape = (s) =>
      String(s == null ? "" : s).replace(/[&<>"']/g, (c) =>
        ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
      );
    const moneyFmt = (window.MinimogSettings && window.MinimogSettings.money_format) || "${{amount}}";
    const formatMoney = (priceFloat) => {
      const cents = Math.round(parseFloat(priceFloat) * 100);
      if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
        try {
          return window.Shopify.formatMoney(cents, moneyFmt);
        } catch (e) {
          // fall through
        }
      }
      return parseFloat(priceFloat).toFixed(2);
    };

    if (!products.length) {
      return '<div data-search-items-wrapper class="m-search-result"></div>';
    }

    const items = products
      .map((p) => {
        const url = `${root}/products/${p.handle}`;
        const img = (p.images && p.images[0] && p.images[0].src) || "";
        const variant = (p.variants && p.variants[0]) || {};
        const price = variant.price !== undefined ? formatMoney(variant.price) : "";
        const compareAt =
          variant.compare_at_price && parseFloat(variant.compare_at_price) > parseFloat(variant.price)
            ? formatMoney(variant.compare_at_price)
            : "";
        const available = (p.variants || []).some((v) => v.available !== false);

        const imgHTML = img
          ? `<img class="m-predictive-search__image" src="${escape(img)}" alt="${escape(
              p.title
            )}" width="70" height="70">`
          : "";
        const soldOutBadge = !available
          ? '<span class="m:flex m-product-tag m-product-tag--soldout">نفذ من المخزون</span>'
          : "";
        const priceHTML = price
          ? `<div class="m-search-item--price"><span class="m-product-price__amount">${escape(price)}</span>${
              compareAt ? `<span class="m-product-price__compare">${escape(compareAt)}</span>` : ""
            }</div>`
          : "";

        return `<a href="${escape(url)}" class="m-search-item m-search-item--with-thumbnail">
      <div class="m-search-item--image">${imgHTML}${soldOutBadge}</div>
      <div class="m-search-item--content">
        <p class="m-search-item--title m:font-medium">${escape(p.title)}</p>
        ${priceHTML}
      </div>
    </a>`;
      })
      .join("");

    return `<div data-search-items-wrapper class="m-search-result">
    <div class="m-search-result__group-wrapper">
      <div class="m-search-result__group">
        <div class="m-search-result__products">
          <h2 class="m-search-result__heading h5">المنتجات</h2>
          <div class="m-search-result__products-list">${items}</div>
        </div>
      </div>
    </div>
  </div>`;
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute("results", true);
    const searchItemsWrapper = this.querySelector("[data-search-items-wrapper]");
    const hasResults = searchItemsWrapper && searchItemsWrapper.childElementCount > 0;
    this.renderSearchQueryAndMessage(hasResults);

    this.toggleSpinnerLoading(false);
    this.toggleClearSearch(true);
    this.open();
  }

  renderSearchQueryAndMessage(results) {
    const { input, searchQuery, searchMessage, moreResultIcon } = this.domNodes;
    const query = input.value;

    const { resultsTitle } = searchMessage.dataset;
    searchQuery.textContent = query;

    this.predictiveSearchResults.classList.remove("m:hidden");
    this.searchPopupSearults.classList.remove("m:hidden");

    if (results) {
      searchMessage.textContent = resultsTitle;
      moreResultIcon.classList.add("m:hidden");
    } else {
      searchMessage.textContent = searchMessage.dataset.resultsTitle;
      moreResultIcon.classList.remove("m:hidden");
    }
  }

  toggleSpinnerLoading(show) {
    const { loading, submit } = this.domNodes;
    submit.style.visibility = show ? "hidden" : "visible";
    loading.style.visibility = show ? "visible" : "hidden";
  }

  toggleClearSearch(show) {
    const { clear } = this.domNodes;
    clear.style.visibility = show ? "visible" : "hidden";
  }

  open() {
    this.searchPopupSearults.style.setProperty(
      "--search-result-max-height",
      `${this.resultsMaxHeight || this.getResultsMaxHeight()}px`
    );
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;

    this.popularSearches && this.popularSearches.classList.add("m:hidden");
    this.searchCount.classList.remove("m:hidden");
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
      this.toggleClearSearch(false);
      this.resultsMaxHeight = false;
    }

    this.removeAttribute("open");
    this.predictiveSearchResults.classList.add("m:hidden");
    this.searchPopupSearults.classList.add("m:hidden");
    this.popularSearches && this.popularSearches.classList.remove("m:hidden");
    this.searchCount.classList.add("m:hidden");
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector("predictive-search").getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }
}

customElements.define("predictive-search", PredictiveSearch);
